import { ChillerLog } from '../../model';
import { isRequiredFieldEmpty } from '../../../utils/highlightedDataUtil';

// A "grid" here is a Cell[][] (react-spreadsheet data), e.g. chillerLog.header_details.
// Returns the display_name of each row containing an empty cell.
const emptyRowLabels = (grid: any): string[] => {
  if (!grid) return [];

  return grid
    .filter((row) => row?.some((cell) => isRequiredFieldEmpty(cell?.value)))
    .map((row) => row?.[0]?.display_name || 'Unlabeled field');
};

const withSection = (section: string, grid: any): string[] =>
  emptyRowLabels(grid).map((label) => `${section}: ${label}`);

// Checks only the current log's own data (never prevChillerLog1/prevChillerLog2), and only the
// sections/circuits/compressors that are actually rendered for the technician to edit.
// Returns a human-readable "Section: Field" entry for every empty required field.
export const getIncompleteRequiredFields = (chillerLog: ChillerLog): string[] => {
  if (!chillerLog) return [];

  const missing: string[] = [
    ...withSection(`${chillerLog.chiller_type_label ?? ''} Cooled Unit`.trim(), chillerLog.header_details),
    ...withSection('Additional Details', chillerLog.additional_details),
  ];

  const showManufacturerDetailsSection = (chillerLog.manufacturer_details?.length ?? 0) > 1;
  if (showManufacturerDetailsSection) {
    missing.push(...withSection('Manufacturer Details', chillerLog.manufacturer_details));
  }

  const showYorkCircuitSection = (chillerLog.circuits?.[0]?.york_circuit_details?.length ?? 0) > 1;

  const circuitsToDisplay = chillerLog.circuits?.slice(0, chillerLog.circuit_count ?? 1) ?? [];

  circuitsToDisplay.forEach((circuit, index) => {
    const circuitNumber = index + 1;

    missing.push(...withSection(`Evaporator Details (Circuit ${circuitNumber})`, circuit?.evaporator_details));
    missing.push(...withSection(`Condenser Details (Circuit ${circuitNumber})`, circuit?.condenser_details));

    const includedCompressors = circuit?.compressors?.filter((compressor) => compressor.included) ?? [];
    includedCompressors.forEach((compressor) => {
      missing.push(
        ...withSection(
          `Compressor Details (Circuit ${circuitNumber}, Comp ${compressor.index + 1})`,
          compressor?.compressor_details,
        ),
      );
    });

    if (showYorkCircuitSection) {
      missing.push(...withSection(`York Circuit Details (Circuit ${circuitNumber})`, circuit?.york_circuit_details));
    }
  });

  return missing;
};
